/**
 * buildOrderSummary — the money on an order, worked out in ONE place.
 *
 * The order detail screen used to read the three parts of one sum from three
 * different cards, and one of the three read the wrong field. The result was a
 * page showing a customer 76,000 + 0 = 87,000, on the screen whose entire job
 * is to explain a charge.
 *
 * The wrong field was the delivery fee. "Shipping Fees" read
 * `shippingAddress.selectedVillage.shippingCost` and `shippingCost_id.cost` —
 * both the LEGACY flat zone fee. The order model marks `shippingCost_id`
 * "@deprecated ... do NOT write new logic against it" and documents
 * `deliveryFee` as authoritative: server-computed at order creation from the
 * signed quote, and the number the customer was actually charged. Nothing on
 * the screen rendered it, so the fee it held fell off the page. `taxPrice`,
 * also a required field on every order, had no line either.
 *
 * Two rules this encodes:
 *
 *   1. Authoritative first, legacy only as a fallback. Orders placed before the
 *      fee engine existed never had `deliveryFee` written, and for those the
 *      legacy pair is still the only truth.
 *   2. Whatever the parts do not explain becomes `adjustment`, which the screen
 *      RENDERS. A summary that quietly refuses to add up is precisely how the
 *      original bug survived; one that names the gap cannot hide the next one.
 *   3. VAT is INCLUSIVE and is therefore NOT one of the parts. Prices on this
 *      platform carry tax inside them: the server writes
 *      `totalPrice = itemsPrice + deliveryFee (+ any processing fee)` and records
 *      `taxPrice` as the tax already sitting inside that, "CONTAINED IN
 *      totalPrice, never added to it". Adding it to the parts made every taxed
 *      order show a phantom `adjustment` of exactly minus the VAT, which the
 *      screen then labelled "Discount". It is reported as an inclusive figure
 *      (`taxIncluded`) so the screen can show it without summing it.
 *
 * @param {object} order an Order document as returned by the API
 * @returns {{itemsPrice:number, taxPrice:number, taxIncluded:boolean,
 *            deliveryFee:number, processingFee:number, processingLabel:string,
 *            totalPrice:number, adjustment:number, unitCount:number,
 *            itemsLabel:string}}
 */
const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const buildOrderSummary = (order) => {
  const itemsPrice = num(order?.itemsPrice);
  const taxPrice = num(order?.taxPrice);
  const totalPrice = num(order?.totalPrice);

  const legacyFee =
    num(order?.shippingAddress?.selectedVillage?.shippingCost) ||
    num(order?.shippingCost_id?.cost);
  const storedFee = num(order?.deliveryFee);
  const deliveryFee = storedFee > 0 ? storedFee : legacyFee;

  /* The customer-facing payment processing fee, present only while Lusuku passes
     the payment-rail charge on rather than absorbing it. Like the delivery fee it
     is CONTAINED IN totalPrice, so it is one of the parts and must be named, or
     it surfaces as an unexplained "Other charges". */
  const processingFee = num(order?.paymentProcessingFee);
  const processingLabel =
    (order?.paymentRail?.feeLabel || "").trim() || "Payment processing fee";

  // Tax is deliberately absent: it is inside itemsPrice, not beside it.
  const adjustment = totalPrice - (itemsPrice + deliveryFee + processingFee);

  // UNITS, not lines. Counting `orderItems.length` reported one line of qty 2
  // as "1 items" — the wrong number and the wrong grammar in four characters.
  const unitCount = (order?.orderItems || []).reduce((n, item) => {
    const q = num(item?.qty);
    return n + (q > 0 ? q : 1);
  }, 0);

  return {
    itemsPrice,
    taxPrice,
    taxIncluded: true,
    deliveryFee,
    processingFee,
    processingLabel,
    totalPrice,
    adjustment,
    unitCount,
    itemsLabel: `${unitCount} item${unitCount === 1 ? "" : "s"}`,
  };
};

export default buildOrderSummary;
